import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { isSearchCrawlerUserAgent } from '../../shared/utils/search-crawler';

/** Keep in sync with index.html early-gate script + server.ts cookie name. */
export const HUMAN_VERIFIED_KEY = 'cs_human_verified';

@Injectable({ providedIn: 'root' })
export class HumanVerifyService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);

  isEnabled(): boolean {
    return !!environment.turnstile?.enabled;
  }

  /** Googlebot / Bing / SEO crawlers — skip Turnstile for indexing. */
  isSearchCrawler(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    try {
      return isSearchCrawlerUserAgent(navigator.userAgent);
    } catch {
      return false;
    }
  }

  isVerified(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    if (this.isSearchCrawler()) {
      return true;
    }
    try {
      if (sessionStorage.getItem(HUMAN_VERIFIED_KEY) === '1') {
        return true;
      }
    } catch {
      /* ignore */
    }
    return this.hasVerifiedCookie();
  }

  markVerified(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      sessionStorage.setItem(HUMAN_VERIFIED_KEY, '1');
    } catch {
      /* ignore quota / private mode */
    }
    // Cookie so SSR / first paint can gate before Angular boots.
    document.cookie = `${HUMAN_VERIFIED_KEY}=1; Path=/; SameSite=Lax`;
  }

  clearVerified(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      sessionStorage.removeItem(HUMAN_VERIFIED_KEY);
    } catch {
      /* ignore */
    }
    document.cookie = `${HUMAN_VERIFIED_KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
  }

  /** Validates Turnstile token via API siteverify. */
  verifyToken(token: string): Observable<boolean> {
    if (!token?.trim()) {
      return of(false);
    }

    return this.http
      .post<{ success?: boolean }>(`${environment.apiUrl}/turnstile/verify`, {
        token: token.trim(),
      })
      .pipe(
        map((res) => {
          const ok = !!res?.success;
          if (ok) {
            this.markVerified();
          }
          return ok;
        }),
        catchError(() => of(false))
      );
  }

  private hasVerifiedCookie(): boolean {
    try {
      return document.cookie
        .split(';')
        .some((part) => part.trim() === `${HUMAN_VERIFIED_KEY}=1`);
    } catch {
      return false;
    }
  }
}
